/**
 * Shared utility for formatting duration values (in minutes)
 * into human-readable strings with locale support (FR/EN).
 *
 * Usage:
 *   import { formatDuration } from '@/utils/duration'
 *
 *   // With vue-i18n instance:
 *   formatDuration(65, i18n)  // → "1 h 05" (FR) or "1 hr 5 min" (EN)
 *
 *   // With locale string:
 *   formatDuration(120, 'fr') // → "2 heures"
 *   formatDuration(120, 'en') // → "2 hours"
 */

const translations = {
    fr: {
        minutesOnly: '{n} min',
        hourSingular: '1 heure',
        hourPlural: '{n} heures',
        hourWithMinutes: '{h} h {m}',
    },
    en: {
        minutesOnly: '{n} min',
        hourSingular: '1 hour',
        hourPlural: '{n} hours',
        hourWithMinutes: '{h} hr {m} min',
    },
}

/**
 * Resolve the current locale string from either a vue-i18n instance or a plain string.
 * Falls back to 'fr' if unrecognized.
 */
function resolveLocale(localeOrI18n) {
    if (!localeOrI18n) return 'fr'
    if (typeof localeOrI18n === 'string') return localeOrI18n
    // vue-i18n instance
    if (localeOrI18n.locale) return localeOrI18n.locale
    return 'fr'
}

/**
 * Format a duration in minutes into a human-readable string.
 *
 * @param {number} minutes - Duration in minutes (integer).
 * @param {object|string} localeOrI18n - A vue-i18n instance or a locale string ('fr'|'en').
 * @returns {string} Formatted duration string.
 *
 * Examples (FR):
 *   formatDuration(55, 'fr')  → "55 min"
 *   formatDuration(60, 'fr')  → "1 heure"
 *   formatDuration(65, 'fr')  → "1 h 05"
 *   formatDuration(70, 'fr')  → "1 h 10"
 *   formatDuration(120, 'fr') → "2 heures"
 *
 * Examples (EN):
 *   formatDuration(55, 'en')  → "55 min"
 *   formatDuration(60, 'en')  → "1 hour"
 *   formatDuration(65, 'en')  → "1 hr 5 min"
 *   formatDuration(70, 'en')  → "1 hr 10 min"
 *   formatDuration(120, 'en') → "2 hours"
 */
export function formatDuration(minutes, localeOrI18n) {
    if (minutes === null || minutes === undefined || minutes <= 0) return ''

    const locale = resolveLocale(localeOrI18n)
    const t = translations[locale] || translations.fr

    const hours = Math.floor(minutes / 60)
    const remainder = minutes % 60

    // Less than 1 hour → show minutes only
    if (hours === 0) {
        return t.minutesOnly.replace('{n}', minutes)
    }

    // Exact hours, no remaining minutes
    if (remainder === 0) {
        if (hours === 1) {
            return t.hourSingular
        }
        return t.hourPlural.replace('{n}', hours)
    }

    // Hours + remaining minutes
    const formattedMinutes = locale === 'fr' ? String(remainder).padStart(2, '0') : String(remainder)
    return t.hourWithMinutes.replace('{h}', hours).replace('{m}', formattedMinutes)
}

export default formatDuration

/**
 * Formate des minutes depuis minuit en "HH:MM".
 *
 * Les créneaux de billetterie peuvent franchir minuit (jusqu'à 1620 = 03:00 le lendemain) : au-delà
 * de 1440, `nextDayLabel` est ajouté, sans quoi une plage 22:00 → 01:00 s'affiche comme un intervalle
 * apparemment inversé. Règle unique, partagée par l'étape « Disponibilités » et sa preview.
 *
 * @param {number} minutes      Minutes depuis minuit (0 → 1620).
 * @param {string} nextDayLabel Marqueur ajouté au-delà de minuit (ex. "(J+1)"). Vide = pas de marqueur.
 * @returns {string}
 */
export function formatMinutesOfDay(minutes, nextDayLabel = '') {
    const value = Number(minutes) || 0
    const label = String(Math.floor(value / 60) % 24).padStart(2, '0') + ':' + String(value % 60).padStart(2, '0')

    return value >= 1440 && nextDayLabel ? `${label} ${nextDayLabel}` : label
}
